import { useCallback, useEffect, useState } from 'react';
import { getAnonymousId } from '@/utils/anonymousId';

/**
 * Per-(viewer, session) notes persisted to localStorage. Anonymous viewers
 * key on their persistent anonymousId; signed-in viewers key on userId so
 * notes follow them across devices once we wire a backend table later.
 *
 * v1 trade-off: localStorage means notes don't roam across devices for the
 * same user yet. A `POST /api/usage-logs/notes` endpoint backed by a small
 * `SessionNote` table is the obvious follow-up.
 */
export function useSessionNotes(sessionId: number | null | undefined, userId?: string | null) {
  const owner = userId ?? `anon:${getAnonymousId()}`;
  const key = sessionId ? `ccms:notes:${owner}:s${sessionId}` : null;

  const [value, setValue] = useState<string>('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Load on mount / when key changes.
  useEffect(() => {
    if (!key) {
      setValue('');
      setSavedAt(null);
      return;
    }
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as { content: string; savedAt: number };
        setValue(parsed.content ?? '');
        setSavedAt(parsed.savedAt ?? null);
      } else {
        setValue('');
        setSavedAt(null);
      }
    } catch {
      setValue('');
      setSavedAt(null);
    }
  }, [key]);

  // Debounced auto-save (450ms) — typing feels live but we don't write per
  // keystroke. The hook only persists when the user actually changes content.
  useEffect(() => {
    if (!key) return;
    const handle = window.setTimeout(() => {
      try {
        const now = Date.now();
        window.localStorage.setItem(key, JSON.stringify({ content: value, savedAt: now }));
        setSavedAt(now);
      } catch {
        // Storage quota or private mode — silently swallow. Notes degrade
        // gracefully instead of blocking the player.
      }
    }, 450);
    return () => window.clearTimeout(handle);
  }, [key, value]);

  const clear = useCallback(() => {
    if (!key) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* see save handler */
    }
    setValue('');
    setSavedAt(null);
  }, [key]);

  return { value, setValue, savedAt, clear };
}
